import React, { useState } from "react";
import AdminHeader from "./AdminHeader.js";
import { useMutation, useQuery } from "@apollo/client";
import {
    ADD_TOPBAR,
    ADD_VALUATION,
    DELETE_TOPBAR,
    GET_VALUATION,
    UPDATE_TOPBAR,
} from "../graphql/Mutation/Topbar.js";
import { toast } from "react-toastify";
import { GET_TOPBAR } from "../graphql/Query/TopBar.js";
import NoDataFound from "../components/NoDataFound.js";
import useScrollToTop from "../components/useScrollToTop.js";
import { FiTrash, FiEdit, FiPlusCircle } from "react-icons/fi";

const AdminTopbar = () => {
    useScrollToTop();

    const [topBarInput, setTopBarInput] = useState({ topBar: "", content: "" });
    const [targetString, setTargetString] = useState("");
    const { data, refetch } = useQuery(GET_TOPBAR);
    const { data: getValuation, refetch: refetchValuation } = useQuery(GET_VALUATION);
    const [addTopBar] = useMutation(ADD_TOPBAR);
    const [editTopBar] = useMutation(UPDATE_TOPBAR);
    const [deleteTopBar] = useMutation(DELETE_TOPBAR);
    const [addValuation] = useMutation(ADD_VALUATION);

    const handleInsertTopBar = async () => {
        try {
            await addTopBar({ variables: { input: { content: targetString } } });
            refetch();
            toast.success("TopBar added successfully!");
            setTargetString("");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleEditTopBar = async (id) => {
        try {
            await editTopBar({ variables: { input: { content: targetString, id } } });
            refetch();
            toast.success("TopBar updated successfully!");
            setTargetString("");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDeleteTopBar = async (id) => {
        try {
            await deleteTopBar({ variables: { deleteTopBarId: id } });
            refetch();
            refetchValuation();
            toast.success("TopBar deleted successfully!");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleAddValuation = async () => {
        if (!topBarInput.topBar || !topBarInput.content) {
            toast.error("Please fill in all fields!");
            return;
        }
        try {
            await addValuation({ variables: { topBar: topBarInput.topBar, content: topBarInput.content } });
            setTopBarInput({ topBar: "", content: "" });
            refetchValuation();
            toast.success("Valuation added successfully!");
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div>
            <AdminHeader />
            <div className="px-4 sm:px-6 lg:px-8 py-10">
                {/* TopBar Display */}
                <div className="mb-4">
                    <p className="flex h-12 items-center justify-center bg-slate-800 text-white text-sm font-medium px-4 rounded-md">
                        {data?.getTopBar?.content || "No TopBar Content"}
                    </p>
                </div>

                {/* TopBar Input Section */}
                <div className="space-y-3">
                    <label className="font-semibold text-lg">TopBar:</label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={targetString}
                            onChange={(e) => setTargetString(e.target.value)}
                            placeholder="Enter TopBar Content"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                        />
                        {data?.getTopBar?.content ? (
                            <button
                                className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-1 hover:bg-blue-700 transition"
                                onClick={() => handleEditTopBar(data?.getTopBar?.id)}
                            >
                                <FiEdit /> Edit
                            </button>
                        ) : (
                            <button
                                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-1 hover:bg-green-700 transition"
                                onClick={handleInsertTopBar}
                            >
                                <FiPlusCircle /> Add
                            </button>
                        )}
                        {data?.getTopBar?.content && (
                            <button
                                className="bg-red-600 text-white px-4 py-2 rounded-md flex items-center gap-1 hover:bg-red-700 transition"
                                onClick={() => handleDeleteTopBar(data?.getTopBar?.id)}
                            >
                                <FiTrash /> Delete
                            </button>
                        )}
                    </div>
                </div>

                {/* Valuation Input Section */}
                <div className="mt-8 space-y-3">
                    <h2 className="font-semibold text-lg">TopBar Management:</h2>
                    <div className="flex flex-col md:flex-row gap-2">
                        <input
                            type="text"
                            placeholder="Title"
                            value={topBarInput.topBar}
                            onChange={(e) => setTopBarInput({ ...topBarInput, topBar: e.target.value })}
                            className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                        />
                        <input
                            type="text"
                            placeholder="Content"
                            value={topBarInput.content}
                            onChange={(e) => setTopBarInput({ ...topBarInput, content: e.target.value })}
                            className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                        />
                        <button
                            onClick={handleAddValuation}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-1 hover:bg-blue-700 transition"
                        >
                            <FiPlusCircle /> Add Valuation
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="overflow-x-auto mt-8 border border-gray-200 rounded-md shadow-sm">
                    <table className="min-w-full bg-white text-sm">
                        <thead>
                            <tr className="bg-slate-100 text-left">
                                <th className="py-3 px-4 font-semibold">No.</th>
                                <th className="py-3 px-4 font-semibold">Title</th>
                                <th className="py-3 px-4 font-semibold">Description</th>
                                <th className="py-3 px-4 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getValuation?.getTopBarValuation?.length > 0 ? (
                                getValuation.getTopBarValuation.map((row, index) => (
                                    <tr key={index} className=" border-b">
                                        <td className="py-3 px-4">{index + 1}</td>
                                        <td className="py-3 px-4">{row?.topBar}</td>
                                        <td className="py-3 px-4">{row?.content}</td>
                                        <td className="py-3 px-4">
                                            <button
                                                onClick={() => handleDeleteTopBar(row?.id)}
                                                className="text-red-600 hover:text-red-800 transition"
                                            >
                                                <FiTrash size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4">
                                        <NoDataFound />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminTopbar;
